import type { Observable } from 'rxjs'
import { throwError, map, tap } from 'rxjs'
import { Injectable, Injector, inject } from '@angular/core'
import type { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse } from '@angular/common/http'
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http'
import { LogService } from '@awork/_shared/services/log-service/log.service'
import { Level } from '@awork/_shared/services/log-service/types'
import { USER_PRODUCT_MISSING_ERROR_CODE } from '@awork/_shared/constants/product-access.constants'
import { ToastService } from '../toast-service/toast.service'

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private injector = inject(Injector)

  private logService: LogService

  /**
   * Passes HTTP requests through and handles response errors for the mobile app.
   * @param {HttpRequest<unknown>} req
   * @param {HttpHandler} next
   * @returns {Observable<HttpEvent<unknown>>}
   */
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    this.logService = this.injector.get(LogService)

    return next.handle(req).pipe(
      tap(
        (event: HttpEvent<unknown>) => {},
        error => {
          if (error instanceof HttpErrorResponse) {
            if (error.status === 0 && error.statusText === 'Unknown Error') {
              return void 0
            }

            if (error.status === 400) {
              this.handleBadRequest(error)
            }

            this.logTrace(error)

            this.logService.saveHttpError(error)
          }

          return throwError(error)
        }
      ),
      map((event: HttpEvent<unknown>) => {
        this.logTrace(event as HttpResponse<unknown>)
        return event
      })
    )
  }

  /**
   * Reports rejected user selections with the required product.
   * @param {HttpErrorResponse} error API validation error.
   */
  private handleBadRequest(error: HttpErrorResponse): void {
    if (error.error?.code === USER_PRODUCT_MISSING_ERROR_CODE) {
      this.injector.get(ToastService).show(q.translations.errors.userProductMissing.description, 'warning')
    }

    this.logService?.logMessage(Level.error, `Bad request: ${error.url} - Code: ${error.error?.code}`, { error })
  }

  /**
   * Logs the trace id of the response
   * @param {HttpResponse<unknown> | HttpErrorResponse} event
   */
  private logTrace(event: HttpResponse<unknown> | HttpErrorResponse): void {
    if (event.headers && event.headers instanceof HttpHeaders) {
      this.logService.logTrace(event.headers.get('trace-id'), event instanceof HttpErrorResponse)
    }
  }
}
